<template>
    <div class="benchmark-pool-fields">
        <info-banner v-if="!hasAccess" severity="warning" role="status">{{ Messages.CustomIndexAccessRequired }}</info-banner>
        <template v-else>
            <info-banner v-if="sharedPoolWarning" severity="warning" role="note">{{ sharedPoolWarning }}</info-banner>
            <fieldset :disabled="isBusy" :inert="isBusy || undefined" class="pool-editor-fields">
                <div class="pool-selector-section">
                    <label class="pool-selector-label" for="benchmarkPoolSelect">Select pool</label>
                    <crud-selector
                        :model-value="selectedPoolId"
                        input-id="benchmarkPoolSelect"
                        name="pool"
                        :headers-by-mode="crudHeadersByMode"
                        :options="poolOptions"
                        selection-placeholder="Select pool"
                        nullable
                        @update:selection="onSelectPool"
                        @create="onCreatePool"
                        @update="onRenamePool"
                        @delete="onDeletePool"
                    />
                </div>
                <template v-if="activeRecord">
                    <div class="preferences-row">
                        <label for="benchmarkPoolName">Pool name</label>
                        <input-text id="benchmarkPoolName" v-model="activeRecord.name" />
                    </div>
                    <div class="preferences-row">
                        <label>Select plant</label>
                        <plant-selector
                            :selected-plant-ids="activeRecord.config.benchmarkPlantId || null"
                            @update:model-value-single="updateBenchmarkPlant"
                        />
                    </div>
                    <div class="preferences-row">
                        <label>Pool size</label>
                        <input-number
                            :model-value="activeRecord.config.requiredPoolSize"
                            :min="1"
                            :max-fraction-digits="0"
                            :use-grouping="false"
                            @update:model-value="updatePoolSize"
                        />
                    </div>
                    <div>
                        <div class="categories-header">
                            <label>Priority categories</label>
                            <info-icon-with-tooltip :tooltip-text="'Higher items are filled first when building the pool.'" />
                        </div>
                        <div ref="categoryWrapperRef" class="benchmark-pool-category-wrapper">
                            <selectable-list-item v-for="(category, index) in orderedCategories" :key="category.id" class="benchmark-pool-category-row">
                                <template #leading><span class="category-priority">{{ index + 1 }}</span></template>
                                {{ getBenchmarkPoolCategoryDisplayLabel(category) }}
                                <template #actions>
                                    <action-button title="Edit category" icon-class-string="fa-solid fa-pencil" @click.stop="openEditCategory(category)" />
                                    <action-button
                                        v-if="orderedCategories.length > 1"
                                        title="Delete category"
                                        class="delete"
                                        icon-class-string="fa-solid fa-trash-can"
                                        @click.stop="confirmDeleteCategory(category)"
                                    />
                                    <div class="draggable" v-tooltip="{ value: 'Click and drag to reorder categories', showDelay: 1000 }">
                                        <i class="fa-solid fa-up-down-left-right" />
                                    </div>
                                </template>
                            </selectable-list-item>
                        </div>
                        <primevue-button class="plain-btn add-category-btn" @click="openAddCategory">
                            Add category <font-awesome-icon :icon="['fas', 'plus']" />
                        </primevue-button>
                    </div>
                    <p class="no-pool-hint">Saving this pool updates the definition used by every leaderboard and index that references it.</p>
                    <p v-if="draftValidation" class="no-pool-hint" role="status">{{ draftValidation }}</p>
                    <div class="pool-draft-actions">
                        <primevue-button :disabled="!!draftValidation || !isDirty" :loading="isSaving" @click="saveDraft">Save pool</primevue-button>
                        <primevue-button class="plain-btn" @click="cancelDraft">Cancel</primevue-button>
                    </div>
                </template>
                <info-banner v-else-if="!poolsLoading && !recordLoading" severity="info" role="status">Select or create a pool to edit its definition.</info-banner>
            </fieldset>
            <info-banner v-if="errorMessage" severity="error" role="alert" class="pool-error">
                <p>{{ errorMessage }}</p>
                <primevue-button v-if="loadFailed" class="plain-btn" :disabled="isBusy" @click="initialise">Retry loading pools</primevue-button>
            </info-banner>
            <benchmark-pool-category-modal
                v-model:visible="showCategoryModal"
                :mode="categoryModalMode"
                :category="categoryBeingEdited"
                @save="onCategorySaved"
            />
        </template>
    </div>
</template>

<script lang="ts" setup>
import {
    computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useConfirm } from 'primevue';
import { useToast } from 'primevue/usetoast';
import InfoBanner from '@/components/common/InfoBanner.vue';
import { useSortable } from '@vueuse/integrations/useSortable';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import PrimevueButton from 'primevue/button';
import PlantSelector from '@/components/powerMarkets/selectors/PlantSelector.vue';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import SelectableListItem from '@/components/common/SelectableListItem.vue';
import CrudSelector from '@/components/powerMarkets/selectors/CrudSelector.vue';
import BenchmarkPoolCategoryModal from '@/components/powerMarkets/BenchmarkPoolCategoryModal.vue';
import {
    IBenchmarkPoolRecord,
    ILeaderboardBenchmarkPoolCategory,
    ILeaderboardBenchmarkPoolConfig,
} from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { createDefaultBenchmarkPoolConfig, getBenchmarkPoolCategoryDisplayLabel } from '@/helpers/powerMarkets/benchmarkPoolHelpers.ts';
import { benchmarkPoolsApi } from '@/api/powerMarkets/benchmarkPoolsApi.ts';
import { cloneDeep } from '@/utils/global.ts';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { TCrudMode } from '@/types/general/TCrudMode.ts';
import { useIndexStore } from '@/stores/benchmarks/indexStore.ts';
import { useBenchmarkPoolSessionStore } from '@/stores/powerMarkets/benchmarkPoolSessionStore.ts';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import { Messages } from '@/constants/Messages.ts';

const props = withDefaults(defineProps<{
    modelValue?: string | null;
    persistSelection?: boolean;
    usageContext?: 'index' | 'leaderboard';
}>(), { modelValue: null, persistSelection: true, usageContext: undefined });

const emit = defineEmits<{
    'update:modelValue': [string | null];
    'update:validation': [string | null];
    'pool-config-changed': [void];
}>();

const confirm = useConfirm();
const toast = useToast();
const sharedPoolWarning = computed(() => {
    if (!props.usageContext) return '';
    const otherFlow = props.usageContext === 'index' ? 'leaderboards' : 'indices';
    return `Pools are shared. Saving changes here also affects all ${otherFlow} using this pool. Deleting it means those ${otherFlow} will need another pool. Pool changes are saved separately from this ${props.usageContext}.`;
});
const indexStore = useIndexStore();
const centralModalStore = useCentralModalStore();
const poolSessionStore = useBenchmarkPoolSessionStore();
const hasAccess = computed(() => indexStore.userCustomAccessIndexCountries.some(country => country.accessKey === 'Gb'));
const poolsLoading = ref(false);
const recordLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const isWriting = computed(() => isSaving.value || isDeleting.value);
const isBusy = computed(() => poolsLoading.value || recordLoading.value || isSaving.value || isDeleting.value);
const poolOptions = ref<Array<IIdName>>([]);
const selectedPoolId = ref<string | null>(props.modelValue);
const activeRecord = ref<IBenchmarkPoolRecord | null>(null);
const savedRecord = ref<IBenchmarkPoolRecord | null>(null);
const categoryWrapperRef = ref<HTMLElement | null>(null);
const categoryOrder = ref<Array<string>>([]);
const showCategoryModal = ref(false);
const categoryModalMode = ref<'add' | 'edit'>('add');
const categoryBeingEdited = ref<ILeaderboardBenchmarkPoolCategory | null>(null);
const errorMessage = ref('');
const loadFailed = ref(false);
let loadSerial = 0;
let disposeCloseGuard: (() => void) | undefined;
let disposeNavigationGuard: (() => void) | undefined;
let pendingDiscard: Promise<boolean> | null = null;
let pendingDefinitionRefresh = false;

const orderedCategories = computed(() => {
    const byId = new Map(activeRecord.value?.config.categories.map(category => [category.id, category]) ?? []);
    return categoryOrder.value.map(id => byId.get(id)).filter((category): category is ILeaderboardBenchmarkPoolCategory => !!category);
});
const isDirty = computed(() => activeRecord.value !== null && JSON.stringify(activeRecord.value) !== JSON.stringify(savedRecord.value));
const hasPendingChanges = computed(() => isDirty.value || isSaving.value || isDeleting.value || showCategoryModal.value);
const crudHeadersByMode = computed<Record<TCrudMode, string>>(() => ({
    create: 'Create new pool draft',
    update: 'Change the pool draft name',
    delete: `Delete pool ${activeRecord.value?.name ?? ''}? Leaderboards and indices using it will need another pool.`,
}));

function setSelectedPoolId (id: string | null): void {
    selectedPoolId.value = id;
    if (props.persistSelection) emit('update:modelValue', id);
}

function restoreRecord (record: IBenchmarkPoolRecord | null): void {
    activeRecord.value = record ? cloneDeep(record) : null;
    categoryOrder.value = record?.config.categories.map(category => category.id) ?? [];
}

function cancelDraft (): void {
    if (isBusy.value) return;
    showCategoryModal.value = false;
    restoreRecord(savedRecord.value);
    setSelectedPoolId(savedRecord.value?.id ?? null);
    errorMessage.value = '';
    loadFailed.value = false;
}

function requestLeave (): Promise<boolean> {
    if (isSaving.value || isDeleting.value) return Promise.resolve(false);
    if (!isDirty.value && !showCategoryModal.value) return Promise.resolve(true);
    if (pendingDiscard) return pendingDiscard;
    pendingDiscard = new Promise<boolean>(resolve => {
        confirm.require({
            message: 'Discard your unsaved pool changes?',
            acceptLabel: 'Discard changes',
            rejectLabel: 'Keep editing',
            accept: () => { cancelDraft(); resolve(true); },
            reject: () => resolve(false),
            onHide: () => resolve(false),
        });
    }).finally(() => { pendingDiscard = null; });
    return pendingDiscard;
}

defineExpose({ requestLeave, hasPendingChanges, isWriting });

function describeError (error: unknown, fallback: string): string {
    const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
    if (typeof data === 'string') return data;
    if (Array.isArray(data) && data.length && data.every(item => typeof item === 'string')) return data.join(' ');
    return error instanceof Error ? error.message : fallback;
}

async function loadActiveRecord (id: string | null): Promise<void> {
    const serial = ++loadSerial;
    savedRecord.value = null;
    restoreRecord(null);
    errorMessage.value = '';
    loadFailed.value = false;
    if (!id || !hasAccess.value) return;
    recordLoading.value = true;
    try {
        const record = await benchmarkPoolsApi.getBenchmarkPool(id);
        if (serial !== loadSerial || !hasAccess.value) return;
        savedRecord.value = cloneDeep(record);
        restoreRecord(record);
    } catch (error) {
        if (serial !== loadSerial) return;
        errorMessage.value = describeError(error, 'The selected pool could not be loaded. Choose another pool or retry.');
        loadFailed.value = true;
    } finally {
        if (serial === loadSerial) recordLoading.value = false;
    }
}

async function initialise (): Promise<void> {
    if (!hasAccess.value || isBusy.value) return;
    poolsLoading.value = true;
    errorMessage.value = '';
    loadFailed.value = false;
    try {
        const pools = await benchmarkPoolsApi.listBenchmarkPools();
        if (!hasAccess.value) return;
        poolOptions.value = pools.map(pool => ({ id: pool.id, name: pool.name }));
    } catch (error) {
        errorMessage.value = describeError(error, 'Pools could not be loaded. Please retry.');
        loadFailed.value = true;
        return;
    } finally {
        poolsLoading.value = false;
    }
    await loadActiveRecord(selectedPoolId.value);
}

const draftValidation = computed<string | null>(() => {
    if (!activeRecord.value) return 'Please select or create a pool.';
    if (!activeRecord.value.name.trim()) return 'A pool name is required.';
    const config = activeRecord.value.config;
    if (!config.benchmarkPlantId.trim()) return 'Please select a benchmark plant.';
    if (!Number.isInteger(config.requiredPoolSize) || config.requiredPoolSize < 1) return 'Pool size must be a whole number of at least 1.';
    if (!config.categories.length) return 'Add at least one priority category.';
    return null;
});
const validationString = computed<string | null>(() => {
    if (!hasAccess.value) return Messages.CustomIndexAccessRequired;
    if (isBusy.value) return 'Wait for the pool operation to finish.';
    if (loadFailed.value) return 'The selected pool could not be loaded. Choose another pool or retry.';
    if (isDirty.value) return 'Save or cancel your pool changes before continuing.';
    return draftValidation.value;
});

async function saveDraft (): Promise<void> {
    if (!hasAccess.value || isBusy.value || draftValidation.value || !isDirty.value || !activeRecord.value) return;
    isSaving.value = true;
    errorMessage.value = '';
    try {
        const draft = cloneDeep(activeRecord.value);
        const saved = draft.id ? await benchmarkPoolsApi.updateBenchmarkPool(draft) : await benchmarkPoolsApi.createBenchmarkPool(draft);
        if (!hasAccess.value) return;
        savedRecord.value = cloneDeep(saved);
        restoreRecord(saved);
        poolOptions.value = [...poolOptions.value.filter(pool => pool.id !== saved.id), { id: saved.id, name: saved.name }]
            .sort((a, b) => a.name.localeCompare(b.name));
        setSelectedPoolId(saved.id);
        emit('pool-config-changed');
        poolSessionStore.notifyDefinitionChanged(saved.id);
    } catch (error) {
        errorMessage.value = describeError(error, 'The pool could not be saved. Your draft is still available; please retry.');
    } finally {
        isSaving.value = false;
    }
}

function updateConfig (partial: Partial<ILeaderboardBenchmarkPoolConfig>): void {
    if (!activeRecord.value || isBusy.value) return;
    activeRecord.value.config = { ...activeRecord.value.config, ...partial };
}

function updateBenchmarkPlant (plant: IPlant | null): void {
    updateConfig({ benchmarkPlantId: plant?.id ?? '' });
}

function updatePoolSize (value: number | null): void {
    updateConfig({ requiredPoolSize: value ?? 0 });
}

async function reorderCategories (): Promise<void> {
    await nextTick();
    if (activeRecord.value && orderedCategories.value.length === activeRecord.value.config.categories.length) {
        updateConfig({ categories: [...orderedCategories.value] });
    }
}

function openAddCategory (): void {
    if (isBusy.value) return;
    categoryModalMode.value = 'add';
    categoryBeingEdited.value = null;
    showCategoryModal.value = true;
}

function openEditCategory (category: ILeaderboardBenchmarkPoolCategory): void {
    if (isBusy.value) return;
    categoryModalMode.value = 'edit';
    categoryBeingEdited.value = cloneDeep(category);
    showCategoryModal.value = true;
}

function onCategorySaved (category: ILeaderboardBenchmarkPoolCategory): void {
    if (!activeRecord.value || isBusy.value) return;
    if (categoryModalMode.value === 'add') {
        updateConfig({ categories: [...activeRecord.value.config.categories, category] });
        categoryOrder.value = [...categoryOrder.value, category.id];
    } else {
        updateConfig({ categories: activeRecord.value.config.categories.map(item => (item.id === category.id ? category : item)) });
    }
}

function confirmDeleteCategory (category: ILeaderboardBenchmarkPoolCategory): void {
    if (!activeRecord.value || isBusy.value || activeRecord.value.config.categories.length <= 1) return;
    confirm.require({
        message: 'Remove this category from the pool draft?',
        accept: () => {
            if (!activeRecord.value || isBusy.value) return;
            updateConfig({ categories: activeRecord.value.config.categories.filter(item => item.id !== category.id) });
            categoryOrder.value = categoryOrder.value.filter(id => id !== category.id);
        },
    });
}

async function onSelectPool (id: string | null): Promise<void> {
    if (id === selectedPoolId.value || !(await requestLeave())) return;
    setSelectedPoolId(id);
    await loadActiveRecord(id);
}

async function onCreatePool (name: string): Promise<void> {
    if (!(await requestLeave())) return;
    restoreRecord({ id: '', name, config: { ...createDefaultBenchmarkPoolConfig(), categories: [] } });
    setSelectedPoolId(null);
}

function onRenamePool (name: string): void {
    if (!activeRecord.value || isBusy.value) return;
    activeRecord.value.name = name;
}

async function onDeletePool (): Promise<void> {
    if (!activeRecord.value?.id || !(await requestLeave())) return;
    const id = activeRecord.value?.id;
    if (!id) return;
    isDeleting.value = true;
    errorMessage.value = '';
    try {
        await benchmarkPoolsApi.deleteBenchmarkPool(id);
        savedRecord.value = null;
        restoreRecord(null);
        setSelectedPoolId(null);
        poolOptions.value = poolOptions.value.filter(pool => pool.id !== id);
        emit('pool-config-changed');
        poolSessionStore.notifyDefinitionChanged(id);
    } catch (error) {
        errorMessage.value = describeError(error, 'The pool could not be deleted. Please retry.');
    } finally {
        isDeleting.value = false;
    }
}

// Other open creation flows and Settings share these saved definitions.
async function refreshSavedPools (): Promise<void> {
    if (!hasAccess.value || isBusy.value) return;
    try {
        const pools = await benchmarkPoolsApi.listBenchmarkPools();
        if (!hasAccess.value) return;
        poolOptions.value = pools.map(pool => ({ id: pool.id, name: pool.name }));
        // A save elsewhere must not replace an unsaved local draft.
        if (hasPendingChanges.value || isBusy.value) return;
        pendingDefinitionRefresh = false;
        if (selectedPoolId.value && !pools.some(pool => pool.id === selectedPoolId.value)) {
            savedRecord.value = null;
            restoreRecord(null);
            setSelectedPoolId(null);
        } else {
            await loadActiveRecord(selectedPoolId.value);
        }
    } catch (error) {
        errorMessage.value = describeError(error, 'Pools could not be refreshed. Please retry.');
        loadFailed.value = true;
    }
}

watch(() => poolSessionStore.definitionVersions, () => {
    // Own writes already update the editor; only react to other editors here.
    if (isWriting.value) return;
    pendingDefinitionRefresh = true;
    refreshSavedPools();
}, { flush: 'sync' });
watch([hasPendingChanges, isBusy], () => {
    if (pendingDefinitionRefresh && !hasPendingChanges.value && !isBusy.value) refreshSavedPools();
});

watch(validationString, value => emit('update:validation', value), { immediate: true });
watch(() => props.modelValue, async id => {
    if (!props.persistSelection || id === selectedPoolId.value) return;
    if (!(await requestLeave())) { emit('update:modelValue', selectedPoolId.value); return; }
    selectedPoolId.value = id;
    await loadActiveRecord(id);
});
watch(hasAccess, allowed => {
    if (allowed) { initialise(); return; }
    ++loadSerial;
    recordLoading.value = false;
    poolOptions.value = [];
    savedRecord.value = null;
    restoreRecord(null);
    errorMessage.value = '';
}, { immediate: true });
watch(categoryWrapperRef, async element => {
    if (!element) return;
    await nextTick();
    useSortable(categoryWrapperRef, categoryOrder, {
        handle: '.draggable', onSort: reorderCategories, animation: 150, ghostClass: 'ghost',
    });
});
// Protect pool drafts when closing either creation flow.
watch([isDirty, isSaving, isDeleting], () => {
    disposeCloseGuard?.();
    disposeCloseGuard = undefined;
    if (!isDirty.value && !isSaving.value && !isDeleting.value) return;
    disposeCloseGuard = centralModalStore.registerModalCloseGuard(() => {
        requestLeave().then(allowed => { if (allowed) centralModalStore.closeCurrentModal(); });
        return false;
    });
});

function handleBeforeUnload (event: BeforeUnloadEvent): void {
    if (!hasPendingChanges.value) return;
    event.preventDefault();
    event.returnValue = '';
}

onMounted(() => {
    if (sharedPoolWarning.value) {
        toast.add({
            severity: 'warn', summary: 'Shared benchmark pool', detail: sharedPoolWarning.value, life: 10000,
        });
    }
    disposeNavigationGuard = centralModalStore.registerNavigationGuard(requestLeave);
    window.addEventListener('beforeunload', handleBeforeUnload);
});
onBeforeUnmount(() => {
    ++loadSerial;
    disposeCloseGuard?.();
    disposeNavigationGuard?.();
    window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<style lang="scss" scoped>
.benchmark-pool-fields {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
}

.pool-editor-fields {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
}

.pool-draft-actions {
    display: flex;
    gap: var(--defaultSpacing);
}

.pool-error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--smallSpacing);

    p {
        margin: 0;
    }
}

.pool-selector-section {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
}

.pool-selector-label {
    font-weight: var(--boldFontWeight);
}

label {
    font-weight: var(--boldFontWeight);
}

.preferences-row {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
}

.categories-header {
    margin-bottom: var(--smallSpacing);
}

.no-pool-hint {
    margin: 0;
    color: var(--neutral600);
    font-size: var(--smallFontSize);
}

.benchmark-pool-category-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    max-height: 280px;
    overflow-y: auto;
}

.benchmark-pool-category-row {
    width: 100%;
}

.category-priority {
    font-weight: var(--boldFontWeight);
    min-width: 1.5rem;
    display: inline-block;
}

.draggable {
    cursor: move;
    color: var(--actionColour);
    padding: var(--p-button-padding-y) var(--p-button-padding-x);
}

.ghost {
    opacity: 0.5;
    background: var(--neutral300);
}

.add-category-btn {
    margin-top: var(--defaultSpacing);
    align-self: flex-start;
}
</style>
